import React, { useMemo, useEffect, useState } from "react";
import "./styles.css";
import { faArrowLeft  } from "@fortawesome/free-solid-svg-icons";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, IDatasource, IGetRowsParams } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import useRouter from "@/store/useRouter";
import { useShallow } from "zustand/shallow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import useAppData from "@/store/useAppData";
import { AG_GRID_LOCALE_CZ } from '@ag-grid-community/locale';
import { FetchItemsFn, FilterKind, Item, UiColumn } from "../ItemList/indexOld";
import Card from "../Card";

const myTheme = themeQuartz.withParams({
    fontSize: 17,
    headerFontSize: 17,
    rowHeight: 55,
    headerHeight: 48,
});

function mapFilter(filter?: FilterKind): ColDef["filter"] {
    if (filter === "text") return "agTextColumnFilter";
    if (filter === "number") return "agNumberColumnFilter";
    if (filter === "set") return "agSetColumnFilter";
    return false;
}

const HistoryItemsOP: React.FC = () => {

    const dateFormat = (date: string, dateOnly?: boolean) => {
        const d = new Date(date);

        const pad = (n: number) => String(n).padStart(2, "0");

        if (dateOnly) {
            return (
                pad(d.getDate()) + "." +
                pad(d.getMonth() + 1) + "." +
                d.getFullYear()
            );
        } else {
            return (
                pad(d.getDate()) + "." +
                pad(d.getMonth() + 1) + "." +
                d.getFullYear() + " " +
                pad(d.getHours()) + ":" +
                pad(d.getMinutes()) + ":" +
                pad(d.getSeconds())
            );
        }
    };

    const actionFormat = (value: string) => {
        if (value === "returnNew") {
            return "nové";
        }

        if (value === "returnUsed") {
            return "použité";
        }

        if (value === "dispose") {
            return "k likvidaci";
        }

        if (value === "notReturned") {
            return "nevráceno";
        }

        if (value === "notReturnedManko") {
            return "nevráceno(pohledávka)";
        }
    };

    const columns: UiColumn<Item>[] = [
        { field: "id", headerName: "Id", flex: 1, filter: "number", floatingFilter: true },
        { field: "name", headerName: "Název", flex: 2, filter: "text", floatingFilter: true },
        { field: "state", headerName: "Stav", flex: 1, filter: "text", floatingFilter: true },
        { field: "list1", headerName: "Kolekce", flex: 1, filter: "text", floatingFilter: true },
        { field: "list2", headerName: "Pohlaví", flex: 1, filter: "text", floatingFilter: true },
        { field: "list3", headerName: "Konfekční velikost", flex: 1, filter: "text", floatingFilter: true },
        { field: "movement", headerName: "Pohyb", flex: 1, filter: "text", floatingFilter: true },
        {
            field: "log_action",
            headerName: "Akce",
            flex: 1,
            filter: "text",
            floatingFilter: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return actionFormat(params.value);
            }
        },
        { field: "quantity", headerName: "Počet", flex: 1, filter: "number", floatingFilter: true },
        { field: "user", headerName: "Zaměstnanec", flex: 1, filter: "text", floatingFilter: true },
        {
            field: "date",
            headerName: "Datum",
            flex: 1,
            filter: 'text',
            floatingFilter: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value);
            }
        },
        { field: "appuser", headerName: "Provedl", flex: 1, filter: "text", floatingFilter: true },
    ];

    const selectedStock = useAppData(state => state.selectedStock);

    const router = useRouter(
        useShallow(state => ({
            go: state.go,
            history: state.history
        }))
    );

    const actions: FloatingAction[] = [
        {
            key: "1",
            label: "Zpět",
            icon: <FontAwesomeIcon icon={faArrowLeft} />,
            onClick: () => router.go({ key: RouterStoreItemKey.LISTITEMS })
        }
    ]

    const columnDefs = useMemo<ColDef<Item>[]>(() => {
        return columns.map((c) => ({
            field: c.field as any,
            headerName: c.headerName,
            flex: c.flex,
            filter: mapFilter(c.filter),
            floatingFilter: c.floatingFilter,
            valueFormatter: c.valueFormatter
        }));
    }, [columns]);

    const datasource = useMemo<IDatasource>(() => ({
        getRows: async (params: IGetRowsParams) => {
            try {
                const result = await fetchItems({
                    startRow: params.startRow,
                    endRow: params.endRow,
                    agParams: { stockId: selectedStock?.id, ...params }
                });

                params.successCallback(result.rows, result.totalCount);
            } catch {
                params.failCallback();
            }
        },
    }), []);

    const fetchItems: FetchItemsFn<Item> = async ({ startRow, endRow, agParams }) => {
        const { rows, totalCount } = await getStockItems(startRow, endRow, { ...agParams });
        return { rows, totalCount };
    };

    const getStockItems = async (startRow, endRow, agParams) => {
        try {
            console.log(agParams);
            const res = await (
                axios({
                    method: "post",
                    url: "api/log/getAll",
                    data: {
                        //filter: fm,
                        //sort: params.sortModel,
                        start: startRow,
                        end: endRow,
                        stockId: agParams.stockId,
                        filterModel: agParams.filterModel,
                        sortModel: agParams.sortModel,
                        withoutQuantity: agParams?.withoutQuantity
                    }
                })
            );

            return {
                rows: res.data.stocksItems,
                totalCount: res.data.totalCount
            };
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    return (<div className={"inventory"}><Card
        title="Skladové pohyby"
    >
        <div className="item-list-card">
            <div className="item-list-grid-wrapper">
                <AgGridReact
                    theme={myTheme}
                    columnDefs={columnDefs}
                    rowModelType="infinite"
                    cacheBlockSize={100}
                    datasource={datasource}
                    localeText={AG_GRID_LOCALE_CZ}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                />
            </div>

            {
                actions?.length ? (
                    <div className="item-list-actions">
                        <FloatingActions actions={actions} />
                    </div>
                ) : null
            }
        </div></Card ></div>
    );
};

export default HistoryItemsOP;